import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy, ArrowLeft, TrendingUp, DollarSign, FileText, Clock,
  Wallet, ShieldCheck, BarChart3,
} from "lucide-react";
import api, { fmtMXN } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const METRICS = [
  { key: "contracted",     label: "Contratado",        unit: "money", icon: DollarSign,   order: "desc", desc: "Suma de todos los contratos firmados/ejecución/concluidos" },
  { key: "invoiced",       label: "Facturado",         unit: "money", icon: FileText,     order: "desc", desc: "Estimaciones autorizadas y pagadas" },
  { key: "paid",           label: "Pagado",            unit: "money", icon: Wallet,       order: "desc", desc: "Solo lo efectivamente cobrado" },
  { key: "balance",        label: "Saldo por cobrar",  unit: "money", icon: TrendingUp,   order: "desc", desc: "Facturado − Pagado" },
  { key: "avg_pay_days",   label: "Días pago",         unit: "days",  icon: Clock,        order: "asc",  desc: "Cliente con MENOS días promedio aparece arriba (mejor pagador)" },
  { key: "contracts_count", label: "# Contratos",      unit: "count", icon: BarChart3,    order: "desc", desc: "Cantidad de contratos firmados" },
];

const TYPE_LABEL = {
  gobierno_federal: "Gob. Federal",
  gobierno_estatal: "Gob. Estatal",
  gobierno_municipal: "Gob. Municipal",
  organismo_publico_descentralizado: "OPD",
  privado_persona_fisica: "Privado PF",
  privado_persona_moral: "Privado PM",
};

function formatVal(value, unit) {
  if (value === null || value === undefined) return "—";
  if (unit === "money") return fmtMXN(value);
  if (unit === "days") return `${value} días`;
  return String(value);
}

export default function ClientsRanking() {
  const [metric, setMetric] = useState("contracted");
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const activeMetric = METRICS.find((m) => m.key === metric) || METRICS[0];
  const order = activeMetric.order;

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get("/contracting-entities/ranking", { params: { metric, limit, order } })
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [metric, limit, order]);

  const Icon = activeMetric.icon;

  return (
    <div data-testid="clients-ranking-page">
      <PageHeader
        subtitle="Visión estratégica del catálogo de Clientes — top performers por métrica"
        title="Top Clientes"
        actions={
          <Link to="/contracting-entities" className="btn-ghost px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Volver a Clientes
          </Link>
        }
      />

      <div className="px-6 lg:px-8 py-6 space-y-5 max-w-6xl">
        {/* Selector de métrica */}
        <div className="panel p-3">
          <div className="label-mono mb-2 flex items-center gap-2">
            <Trophy className="w-3.5 h-3.5" style={{ color: "var(--magenta)" }} /> ORDENAR POR
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {METRICS.map((m) => {
              const MIcon = m.icon;
              const active = m.key === metric;
              return (
                <button
                  key={m.key}
                  data-testid={`metric-${m.key}`}
                  onClick={() => setMetric(m.key)}
                  className="px-3 py-2 text-[11px] uppercase tracking-widest rounded-sm flex items-center justify-center gap-1.5 transition-all"
                  style={{
                    background: active ? "rgba(34,211,238,0.15)" : "rgba(15,23,42,0.6)",
                    color: active ? "var(--cyan)" : "#94a3b8",
                    border: active ? "1px solid var(--cyan)" : "1px solid var(--border)",
                    fontWeight: active ? 700 : 500,
                  }}
                >
                  <MIcon className="w-3.5 h-3.5" /> {m.label}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500 gap-2">
            <span>{activeMetric.desc}</span>
            <label className="flex items-center gap-2 shrink-0">
              <span>Top</span>
              <select
                data-testid="limit-select"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                className="input-cyber px-2 py-1 text-xs"
              >
                {[5, 10, 20, 50].map((n) => (
                  <option key={n} value={n} style={{ background: "#0a0f1d" }}>{n}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {loading ? (
          <div className="panel p-10 text-center text-slate-400">Cargando ranking…</div>
        ) : !data || !data.items || data.items.length === 0 ? (
          <div className="panel p-10 text-center text-slate-500">
            <Trophy className="w-12 h-12 mx-auto mb-3 text-slate-700" />
            Aún no hay datos suficientes para este ranking. Crea contratos y estimaciones para alimentar las métricas.
          </div>
        ) : (
          <div className="panel" data-testid="ranking-table">
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" style={{ color: "var(--cyan)" }} />
                <span className="font-display font-bold text-sm text-slate-200">
                  Ranking por {activeMetric.label}
                </span>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-slate-500">
                {data.items.length} de {data.total_entities} clientes
              </span>
            </div>

            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {data.items.map((row, idx) => {
                const val = row[
                  {
                    contracted: "total_contracted",
                    invoiced: "total_invoiced",
                    paid: "total_paid",
                    balance: "balance",
                    avg_pay_days: "avg_pay_days",
                    contracts_count: "contracts_count",
                  }[metric]
                ];
                const numericVal = typeof val === "number" ? val : 0;
                const pct = data.max_value > 0 ? Math.max(2, (Math.abs(numericVal) / data.max_value) * 100) : 0;
                const isTop1 = idx === 0;
                const isTop3 = idx < 3;
                const rankColor = idx === 0 ? "#facc15" : idx === 1 ? "#cbd5e1" : idx === 2 ? "#fb923c" : "#475569";

                return (
                  <Link
                    key={row.entity_id}
                    to={`/contracting-entities/${row.entity_id}`}
                    data-testid={`ranking-row-${row.entity_id}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-cyan-500/5 transition-colors"
                  >
                    {/* Posición */}
                    <div
                      className="w-9 h-9 rounded-sm flex items-center justify-center font-mono font-bold text-sm shrink-0"
                      style={{
                        background: isTop3 ? `${rankColor}22` : "rgba(15,23,42,0.7)",
                        color: rankColor,
                        border: `1px solid ${rankColor}55`,
                      }}
                    >
                      {idx + 1}
                    </div>

                    {/* Nombre + meta */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-bold text-sm text-slate-100 truncate">
                          {row.entity_short_name ? `${row.entity_short_name} — ` : ""}
                          {row.entity_name}
                        </div>
                        {row.entity_auditable && (
                          <ShieldCheck className="w-3 h-3 shrink-0" style={{ color: "var(--magenta)" }} title="Auditable" />
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-500">
                        <span>{TYPE_LABEL[row.entity_type] || row.entity_type}</span>
                        <span>· {row.contracts_count} contrato{row.contracts_count !== 1 ? "s" : ""}</span>
                        {row.projects_count > 0 && <span>· {row.projects_count} proyecto{row.projects_count !== 1 ? "s" : ""}</span>}
                        {row.avg_pay_days !== null && row.avg_pay_days !== undefined && (
                          <span className="flex items-center gap-0.5">
                            · <Clock className="w-3 h-3" /> {row.avg_pay_days}d pago
                          </span>
                        )}
                      </div>
                      {/* Barra visual */}
                      <div className="mt-1.5 h-1.5 rounded-sm overflow-hidden" style={{ background: "rgba(15,23,42,0.8)" }}>
                        <div
                          className="h-full transition-all"
                          style={{
                            width: `${pct}%`,
                            background: isTop1
                              ? "linear-gradient(90deg, var(--cyan), var(--magenta))"
                              : isTop3
                              ? "var(--cyan)"
                              : "rgba(34,211,238,0.5)",
                          }}
                        />
                      </div>
                    </div>

                    {/* Valor principal */}
                    <div className="text-right shrink-0 min-w-[130px]">
                      <div
                        className="font-mono font-bold text-base tabular-nums"
                        style={{ color: isTop3 ? "var(--cyan)" : "#e2e8f0" }}
                        data-testid={`ranking-value-${row.entity_id}`}
                      >
                        {formatVal(val, activeMetric.unit)}
                      </div>
                      {metric !== "contracted" && row.total_contracted > 0 && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          Contr.: {fmtMXN(row.total_contracted)}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Resumen agregado */}
        {data && data.items && data.items.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ranking-totals">
            <Kpi label="Σ Contratado" value={fmtMXN(sumKey(data.items, "total_contracted"))} />
            <Kpi label="Σ Facturado" value={fmtMXN(sumKey(data.items, "total_invoiced"))} />
            <Kpi label="Σ Pagado" value={fmtMXN(sumKey(data.items, "total_paid"))} />
            <Kpi
              label="Σ Saldo"
              value={fmtMXN(sumKey(data.items, "balance"))}
              tone={sumKey(data.items, "balance") > 0 ? "warn" : "ok"}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function sumKey(arr, key) {
  return arr.reduce((s, x) => s + (Number(x[key]) || 0), 0);
}

function Kpi({ label, value, tone }) {
  const color = tone === "warn" ? "#fbbf24" : tone === "ok" ? "#4ade80" : "var(--cyan)";
  return (
    <div className="panel p-3">
      <div className="label-mono text-[10px]">{label}</div>
      <div className="font-mono font-bold text-sm tabular-nums mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
